import { faChevronDown, faChevronUp } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Checkbox, ClickAwayListener, Typography } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import Pet from '@spike/pet-model';
import useNonInitialEffect from '@versiondos/hooks';
import clsx from 'clsx';
import { useFieldStyles } from 'components/UI/FieldStyles';
import LabelField from 'components/UI/LabelField';
import { FunctionComponent, useState } from 'react';
import isEqual from 'lodash/isEqual';

export interface SelectMultiplePetsProps {
    label?: string | JSX.Element;
    name?: string;
    selectedPetIds: Array<number>;
    placeholder?: string;
    pets: Array<Pet>;
    disabled?: boolean;
    required?: boolean;
    id?: string;
    className?: string;
    onSelectionChange: (petIds: Array<number>) => void;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            minWidth: 110
        },
        dropdownContainer: {
            position: 'relative',
            height: 54
        },
        collapsedContainer: {
            height: '100%'
        },
        expandedContainer: {
            display: 'flex',
            flexDirection: 'column',
            position: 'absolute',
            top: 0,
            left: 0,
            zIndex: 10,
            width: '100%',
            backgroundColor: 'white',
            border: '1px solid #222222 !important',
            paddingBottom: 30,
            [theme.breakpoints.up('md')]: {
                paddingBottom: 33
            }
        },
        headerContainer: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
            height: 49,
            cursor: 'pointer'
        },
        header: {
            display: 'flex',
            alignItems: 'center',
            color: '#000000',
            fontSize: 16,
            lineHeight: '27px',
            paddingLeft: 17,
            [theme.breakpoints.up('md')]: {
                paddingLeft: 19
            }
        },
        listContainer: {
            borderTop: '1px solid #222222',
            overflowY: 'auto',
            height: 'auto',
            paddingTop: 13,
            maxHeight: 200,
            [theme.breakpoints.up('md')]: {
                paddingTop: 14
            }
        },
        placeholder: {
            color: '#7A7A7A',
            fontSize: 16,
            lineHeight: '27px'
        },
        border: {
            border: '1px solid #222222',
            borderRadius: 27,
            [theme.breakpoints.up('md')]: {
                borderRadius: 30
            }
        },
        textContainer: {
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            height: '100%',
            paddingRight: 10
        },
        chevronContainer: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            paddingRight: 32,
            [theme.breakpoints.up('md')]: {
                paddingRight: 35
            }
        },
        itemContainer: {
            'display': 'flex',
            'flexDirection': 'column',
            'justifyContent': 'center',
            'height': 33,
            '&:hover': {
                background: '#F8F8F8'
            }
        },
        itemContent: {
            cursor: 'pointer',
            display: 'flex',
            justifyContent: 'space-between',
            paddingLeft: 13,
            paddingRight: 14,
            [theme.breakpoints.up('md')]: {
                paddingLeft: 14,
                paddingRight: 16
            }
        },
        item: {
            padding: '3px 5px',
            fontSize: 16,
            lineHeight: '16px',
            [theme.breakpoints.up('md')]: {
                padding: '4px 6px'
            }
        },
        optionNotSelected: {
            border: '1px solid #D3D3D3 !important'
        },
        petIcon: {
            width: 50,
            height: 24,
            padding: '3px 12px 3px 12px',
            borderRadius: 18,
            [theme.breakpoints.up('md')]: {
                width: 52,
                height: 26,
                padding: '3px 14px 3px 14px',
                borderRadius: 20
            },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
        },
        petIconDogs: {
            color: '#5e8677',
            backgroundColor: '#e9f0ed'
        },
        petIconCats: {
            color: '#c18733',
            backgroundColor: '#faefdf'
        },
        petIconOthers: {
            color: '#9c8267',
            backgroundColor: '#ebe7e3'
        },
        chip: {
            fontSize: 14,
            lineHeight: '14px'
        },
        chevron: {
            fontSize: 16
        },
        nameContainer: {
            display: 'flex',
            alignItems: 'center'
        },
        checkbox: {
            color: 'black !important',
            padding: 0
        }
    })
);

export const SelectMultiplePets: FunctionComponent<SelectMultiplePetsProps> = props => {
    const classes = useStyles();
    const fieldClasses = useFieldStyles();

    const [isCollapsed, setIsCollapsed] = useState(true);
    const [selectedIds, setSelectedIds] = useState([...props.selectedPetIds]);

    useNonInitialEffect(() => {
        !isEqual(props.selectedPetIds, selectedIds) && setSelectedIds([...props.selectedPetIds]);
    }, [props.selectedPetIds]);

    const clickCollapseHandler = () => {
        setIsCollapsed(prev => !prev);
    };

    const toggleHandler = (selectedId: number) => {
        const ids = selectedIds.some(id => id === selectedId)
            ? selectedIds.filter(id => id !== selectedId)
            : [...selectedIds, selectedId];
        setSelectedIds(ids);
        props.onSelectionChange(ids);
    };

    const selectAllHandler = () => {
        const ids = props.pets.length === selectedIds.length ? [] : props.pets.map(pet => pet.id ?? 0);
        setSelectedIds(ids);
        props.onSelectionChange(ids);
    };

    const header = (
        <Box
            className={classes.headerContainer}
            onClick={clickCollapseHandler}
        >
            <Box className={classes.textContainer}>
                <Typography className={clsx(classes.header, classes.placeholder)}>
                    {props.placeholder ?? 'Select Pet(s)'}
                </Typography>
            </Box>
            <Box className={classes.chevronContainer}>
                <FontAwesomeIcon
                    icon={isCollapsed ? faChevronDown : faChevronUp}
                    color={isCollapsed ? '#7A7A7A' : '#222222'}
                    className={classes.chevron}
                />
            </Box>
        </Box>
    );

    const list = (
        <Box className={classes.listContainer}>
            <Box
                className={classes.itemContainer}
                id={`selection_pet_all`}
            >
                <Box
                    className={classes.itemContent}
                    onClick={selectAllHandler}
                >
                    <Box className={classes.nameContainer}>
                        <Checkbox
                            checked={props.pets.length === selectedIds.length}
                            className={classes.checkbox}
                            disableRipple={true}
                            disableFocusRipple={true}
                            disableTouchRipple={true}
                        />
                        <Typography className={classes.item}>All Pets</Typography>
                    </Box>
                </Box>
            </Box>

            {props.pets.map((pet, index) => (
                <Box
                    className={classes.itemContainer}
                    key={pet.id}
                    id={`selection_pet_${index}`}
                >
                    <Box
                        className={classes.itemContent}
                        onClick={() => toggleHandler(pet.id ?? 0)}
                    >
                        <Box className={classes.nameContainer}>
                            <Checkbox
                                checked={selectedIds.includes(pet.id ?? 0)}
                                className={classes.checkbox}
                                disableRipple={true}
                                disableFocusRipple={true}
                                disableTouchRipple={true}
                            />
                            <Typography className={classes.item}>{pet.name}</Typography>
                        </Box>
                        <Box
                            className={clsx([
                                classes.petIcon,
                                { [classes.petIconDogs]: pet.type?.id === 'dogs' },
                                { [classes.petIconCats]: pet.type?.id === 'cats' },
                                { [classes.petIconOthers]: pet.type?.id === 'exotics' }
                            ])}
                        >
                            <Typography className={classes.chip}>{pet.type?.plural}</Typography>
                        </Box>
                    </Box>
                </Box>
            ))}
        </Box>
    );

    const collapsed = (
        <Box className={clsx(classes.border, classes.collapsedContainer, [classes.optionNotSelected])}>{header}</Box>
    );

    const expanded = (
        <Box className={clsx(classes.border, classes.expandedContainer)}>
            {header}
            {list}
        </Box>
    );

    return (
        <ClickAwayListener onClickAway={() => setIsCollapsed(true)}>
            <Box className={clsx(classes.container, fieldClasses.container, props.className)}>
                <LabelField {...props} />
                <Box
                    className={classes.dropdownContainer}
                    id="select_multiplepets"
                >
                    {isCollapsed ? collapsed : expanded}
                </Box>
            </Box>
        </ClickAwayListener>
    );
};

export default SelectMultiplePets;
